import type { LinkInfo, SessionView } from "@gsignatures/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isFailure, type ApiFailure, type SignerApi } from "./api/types";
import { Banner } from "./components/Banner";
import { DeclineModal, DownloadModal, HelpModal, PaperModal } from "./components/Modals";
import { Toast } from "./components/Toast";
import { TopBar, type MenuAction } from "./components/TopBar";
import { TIMING } from "./config";
import { who } from "./lib/format";
import { useAnswers } from "./state/answers";
import { formOf, lettersOf, screenFor, stepIndexFor, uploadsOf, type FlowCtx, type ModalKind, type Screen } from "./state/flow";
import { ConsentScreen } from "./screens/ConsentScreen";
import { DoneScreen } from "./screens/DoneScreen";
import { OfferScreen } from "./screens/OfferScreen";
import { UploadScreen } from "./screens/UploadScreen";
import { ReviewScreen } from "./screens/ReviewScreen";
import { SignScreen } from "./screens/SignScreen";
import { DeadLinkScreen, StateScreen } from "./screens/StateScreen";
import { TaxScreen } from "./screens/TaxScreen";
import { VerifyScreen } from "./screens/VerifyScreen";
import { S } from "./strings";

type Phase = "loading" | "dead" | "state" | "verify" | "flow";
type BannerState = { kind: "offline"; retry?: () => void } | { kind: "rate" } | { kind: "session" };

const isSession = (x: unknown): x is SessionView => !!x && typeof x === "object" && "step" in x && "documents" in x;
const isLink = (x: unknown): x is LinkInfo => !!x && typeof x === "object" && "state" in x && "sender" in x && !("documents" in x);

/** The signing page for one link: decides what to show, and handles every API failure in one place. */
export function SignerApp({ api }: { api: SignerApi }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [link, setLink] = useState<LinkInfo | null>(null);
  const [session, setSession] = useState<SessionView | null>(null);
  const [screen, setScreen] = useState<Screen>("consent");
  const [modal, setModal] = useState<ModalKind | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [banner, setBanner] = useState<BannerState | null>(null);
  const [editField, setEditField] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** When the server's session lapses, pushed forward by every save (each one counts as activity). */
  const [sessionEnd, setSessionEnd] = useState<number | null>(null);
  const sessionSpan = useRef(30 * 60_000);

  const toast = useCallback((m: string) => {
    setToastMsg(m);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(null), TIMING.toastMs);
  }, []);

  const takeSession = useCallback((s: SessionView) => {
    setSession(s);
    const end = new Date(s.sessionExpiresAt).getTime();
    if (!Number.isNaN(end)) {
      sessionSpan.current = Math.max(60_000, end - Date.now());
      setSessionEnd(end);
    }
  }, []);

  const handlerRef = useRef<(e: unknown, retry?: () => void) => void>(() => undefined);

  const enter = useCallback(async () => {
    try {
      const l = await api.link();
      setLink(l);
      setBanner((b) => (b?.kind === "offline" ? null : b));
      if (l.state !== "ready") {
        let s: SessionView | null = null;
        if (l.session && (l.state === "completed" || l.state === "paper")) s = await api.session().catch(() => null);
        if (s) takeSession(s);
        else setSession(null);
        if (l.state === "completed" && s) {
          setScreen("done");
          setPhase("flow");
        } else setPhase("state");
        return;
      }
      if (!l.session) {
        setPhase("verify");
        return;
      }
      const s = await api.session();
      takeSession(s);
      setScreen(screenFor(s.step));
      setPhase("flow");
    } catch (e) {
      handlerRef.current(e, () => void enter());
    }
  }, [api, takeSession]);

  const refresh = useCallback(async () => {
    try {
      const s = await api.session();
      takeSession(s);
      setScreen(screenFor(s.step));
    } catch (e) {
      handlerRef.current(e);
    }
  }, [api, takeSession]);

  const handle = useCallback(
    (e: unknown, retry?: () => void) => {
      if (!isFailure(e)) {
        toast(S.errors.generic);
        return;
      }
      switch (e.code) {
        case "invalid_link":
          setModal(null);
          setPhase("dead");
          break;
        case "session_required":
          setModal(null);
          setLink((l) => (l ? { ...l, session: false } : l));
          setPhase("verify");
          toast(S.errors.sessionEnded);
          break;
        case "paused":
          setModal(null);
          void enter();
          break;
        case "rate_limited":
          setBanner({ kind: "rate" });
          break;
        case "wrong_step":
        case "conflict":
          toast(S.errors.outOfDate);
          void refresh();
          break;
        case "preview_mismatch":
          toast(S.errors.previewMismatch);
          void refresh();
          break;
        case "invalid_values":
          toast(S.errors.invalidValues);
          setScreen("tax");
          break;
        case "network":
          setBanner(retry ? { kind: "offline", retry } : { kind: "offline" });
          break;
        default:
          toast(S.errors.generic);
      }
    },
    [enter, refresh, toast],
  );
  handlerRef.current = handle;

  useEffect(() => {
    void enter();
  }, [enter]);

  /** The letter opened from the review; null = whichever the step names (or the last one). */
  const [letterView, setLetterView] = useState<string | null>(null);
  const apply = useCallback(
    (s: SessionView) => {
      takeSession(s);
      setLetterView(null);
      setScreen(screenFor(s.step));
    },
    [takeSession],
  );

  const run = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        const r = await fn();
        setBanner((b) => (b?.kind === "offline" || b?.kind === "rate" ? null : b));
        return r;
      } catch (e) {
        handle(e, async () => {
          setBanner(null);
          const r = await run(fn);
          if (isSession(r)) apply(r);
          else if (isLink(r)) void enter();
        });
        return undefined;
      }
    },
    [handle, apply, enter],
  );

  const onFatal = useCallback((e: ApiFailure) => handle(e), [handle]);
  const onSaved = useCallback(() => setSessionEnd(Date.now() + sessionSpan.current), []);
  const formDoc = session ? formOf(session) : undefined;
  const { answers, saveStatus } = useAnswers(api, formDoc, onFatal, onSaved);

  // Warn shortly before the session lapses, so nobody loses their place without notice.
  useEffect(() => {
    if (!sessionEnd || phase !== "flow" || screen === "done") return;
    const wait = sessionEnd - TIMING.sessionWarnMs - Date.now();
    const t = setTimeout(() => setBanner((b) => b ?? { kind: "session" }), Math.max(0, wait));
    return () => clearTimeout(t);
  }, [sessionEnd, phase, screen]);
  useEffect(() => {
    if (banner?.kind === "session" && sessionEnd && sessionEnd - Date.now() > TIMING.sessionWarnMs) setBanner(null);
  }, [sessionEnd, banner]);

  // Each new screen starts at the top.
  useEffect(() => {
    window.scrollTo?.(0, 0);
  }, [screen, phase]);

  const sender = session?.sender ?? link?.sender ?? null;
  useEffect(() => {
    document.title = sender ? `Review and sign · ${sender.name}` : "gSignatures";
  }, [sender]);

  const go = useCallback((s: Screen) => {
    setModal(null);
    setScreen(s);
  }, []);
  const viewLetter = useCallback((id: string) => {
    setModal(null);
    setLetterView(id);
    setScreen("offer");
  }, []);
  const continueFrom = useCallback(
    (id: string) => {
      const all = session ? lettersOf(session) : [];
      const next = all[all.findIndex((l) => l.id === id) + 1];
      if (next) viewLetter(next.id);
      else {
        setLetterView(null);
        go("tax");
      }
    },
    [session, viewLetter, go],
  );

  const ctx: FlowCtx | null = useMemo(
    () => (session ? { api, session, w: who(session.sender), run, apply, update: takeSession, go, viewLetter, continueFrom, toast, openModal: setModal } : null),
    [api, session, run, apply, takeSession, go, viewLetter, continueFrom, toast],
  );

  const onEdit = useCallback((id: string) => {
    setEditField(id);
    setScreen("tax");
  }, []);
  const onEdited = useCallback(() => setEditField(null), []);

  const toLinkState = (l: LinkInfo) => {
    setLink(l);
    setModal(null);
    setPhase("state");
  };

  // The letter on the reading screen: the one opened from the review, else the one the step names, else the last.
  const letters = session ? lettersOf(session) : [];
  const step = session?.step;
  const stepLetter = step?.step === "read" ? letters.find((l) => l.id === step.documentId) : undefined;
  const letter = letters.find((l) => l.id === letterView) ?? stepLetter ?? letters[letters.length - 1];
  const uploads = session ? uploadsOf(session) : [];
  const stepUpload = step?.step === "upload" ? uploads.find((u) => u.id === step.documentId) : undefined;
  let body: React.ReactNode = null;
  if (phase === "loading") {
    body = (
      <main className="page" id="main" aria-busy="true">
        <div className="panel panel-pad center-card skeleton-card">
          <div className="sk sk-ico" />
          <div className="sk sk-title" />
          <div className="sk sk-line" />
          <div className="sk sk-line short" />
          <span className="sr">{S.common.loading}</span>
        </div>
      </main>
    );
  } else if (phase === "dead") {
    body = <DeadLinkScreen />;
  } else if (phase === "state" && link) {
    body = <StateScreen api={api} link={link} session={session} onVerify={() => setPhase("verify")} />;
  } else if (phase === "verify" && link) {
    body = <VerifyScreen api={api} link={link} onVerified={() => void enter()} onPaused={() => void enter()} onError={handle} />;
  } else if (phase === "flow" && ctx && session) {
    if (screen === "consent") body = <ConsentScreen ctx={ctx} />;
    else if (screen === "offer" && letter) body = <OfferScreen key={letter.id} ctx={ctx} doc={letter} />;
    else if (screen === "tax" && formDoc) body = <TaxScreen ctx={ctx} doc={formDoc} answers={answers} editField={editField} onEdited={onEdited} />;
    else if (screen === "upload" && uploads[0]) body = <UploadScreen ctx={ctx} doc={stepUpload ?? uploads[0]} />;
    else if (screen === "review") body = <ReviewScreen ctx={ctx} letters={letters} form={formDoc} uploads={uploads} answers={answers} onEdit={onEdit} />;
    else if (screen === "sign" && letters.length > 0) body = <SignScreen ctx={ctx} letters={letters} form={formDoc} answers={answers} />;
    else body = <DoneScreen api={api} session={session} />;
  }

  const stepIndex = phase === "verify" ? 0 : phase === "flow" ? stepIndexFor(screen) : null;
  const menuOn = phase === "flow" && screen !== "done";
  const onMenu = (a: MenuAction) => setModal(a);
  const w = sender ? who(sender) : null;

  return (
    <div className="app">
      <a className="skip" href="#main">
        Skip to content
      </a>
      <TopBar sender={phase === "dead" ? null : sender} stepIndex={stepIndex} save={phase === "flow" && screen !== "done" ? saveStatus : "idle"} onHelp={w && phase !== "dead" ? () => setModal("help") : undefined} onMenu={menuOn ? onMenu : undefined} />
      {banner?.kind === "offline" && (
        <Banner
          icon="wifi"
          tone="err"
          action={
            <button type="button" className="btn btn-quiet btn-sm" onClick={() => (banner.retry ? banner.retry() : setBanner(null))}>
              {S.common.retry}
            </button>
          }
        >
          {S.errors.offline}
        </Banner>
      )}
      {banner?.kind === "rate" && (
        <Banner icon="clock" tone="flag" action={<button type="button" className="btn-link" onClick={() => setBanner(null)}>{S.common.close}</button>}>
          {S.errors.rateLimited}
        </Banner>
      )}
      {banner?.kind === "session" && (
        <Banner
          icon="clock"
          tone="info"
          action={
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              onClick={() => {
                setBanner(null);
                void refresh();
              }}
            >
              {S.session.keepGoing}
            </button>
          }
        >
          {S.session.endingSoon}
        </Banner>
      )}
      {body}
      {modal === "help" && w && <HelpModal w={w} onClose={() => setModal(null)} toast={toast} />}
      {modal === "paper" && (
        <PaperModal
          onClose={() => setModal(null)}
          onConfirm={async () => {
            const l = await run(() => api.paper());
            if (l) toLinkState(l);
          }}
        />
      )}
      {modal === "decline" && w && (
        <DeclineModal
          w={w}
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            const l = await run(() => api.decline(reason || undefined));
            if (l) toLinkState(l);
          }}
        />
      )}
      {modal === "download" && session && <DownloadModal api={api} session={session} onClose={() => setModal(null)} />}
      <Toast message={toastMsg} />
    </div>
  );
}
